/**
 * <quick-buy-sizes> Custom Element
 *
 * Handles:
 * 1. Integrated colour swatch clicks → update size buttons (variant colours)
 *    or fetch related product variants → rebuild size buttons
 * 3. Also listens for external swatch clicks from product-card-swatches
 */

class QuickBuySizes extends HTMLElement {
  connectedCallback() {
    /** @type {Array<{id:number, option1:string, option2:string|null, available:boolean, featured_image:{src:string}|null, pim_product_id:string|null, hscode:string|null, country_of_origin:string|null, gtin:string|null, localised_rates:object|null}>} */
    this._variants = [];

    try {
      this._variants = JSON.parse(this.dataset.variants || "[]");
    } catch {
      this._variants = [];
    }

    this._selectedColor = this.dataset.selectedColor || null;
    this._marketKey = this.dataset.marketKey || null;

    this._sizeIndex = parseInt(this.dataset.sizeIndex || "1", 10);
    this._colorIndex = parseInt(this.dataset.colorIndex || "2", 10);
    this._fitIndex = parseInt(this.dataset.fitIndex || "0", 10);

    // Multi-axis (Size + Fit) paged flow state
    this._selectedSize = null;
    this._selectedFit = null;
    this._page = "size";

    if (!window.globalQuickBuyVariantCache) {
      window.globalQuickBuyVariantCache = new Map();
    }
    this._variantCache = window.globalQuickBuyVariantCache;

    const productCard = this.closest("product-card");
    // Cache original product image
    const img = productCard?.querySelector(".product-media__image");
    if (img) {
      this._originalImageSrc = img.src;
      this._originalImageSrcset = img.srcset || "";
    }

    if (productCard) {
      // Listen for external swatch clicks
      productCard.addEventListener(
        "click",
        this._onExternalSwatchClick.bind(this),
      );
    }

    // Cache current product variants
    const handle = this.dataset.productHandle;
    if (handle) {
      this._variantCache.set(handle, {
        variants: this._variants,
        featured_image: this._originalImageSrc || null,
      });
    }

    // Cache the sizes list and move it to the card gallery
    this.sizesList = this.querySelector(".product-card__quick-buy-sizes");
    const gallery = productCard?.querySelector(".card-gallery");
    if (gallery && this.sizesList) {
      gallery.appendChild(this.sizesList);
    }

    // Multi-axis (Size + Fit) paged overlay: cache page elements + nav arrows
    this._multiAxis = this.sizesList?.dataset.multiAxis === "true";
    if (this._multiAxis && this.sizesList) {
      this._sizeOptionsList = this.sizesList.querySelector(
        ".product-card__quick-buy-page--size .product-card__quick-buy-options",
      );
      this._fitOptionsList = this.sizesList.querySelector(
        ".product-card__quick-buy-page--fit .product-card__quick-buy-options",
      );

      // Reset to the size page whenever the card stops being hovered so the
      // overlay always reopens on step 1.
      if (productCard) {
        productCard.addEventListener("mouseleave", () => this._resetPaged());
      }
    }

    // Bind click events (delegated)
    this.addEventListener("click", this._onClick.bind(this));
    if (this.sizesList) {
      this.sizesList.addEventListener("click", this._onClick.bind(this));
    }

    // Handle outside clicks to close the overlay
    this._onOutsideClick = this._onOutsideClick.bind(this);
    document.addEventListener("click", this._onOutsideClick);

    // Pre-fetch related products when card is close to viewport
    this._setupPreFetch();
  }

  /**
   * Setup IntersectionObserver to pre-fetch related product variants
   */
  _setupPreFetch() {
    const relatedSwatches = this.querySelectorAll(
      '.product-card__quick-buy-colour[data-swatch-type="related"]',
    );
    if (relatedSwatches.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            relatedSwatches.forEach((swatch) => {
              const handle = swatch.dataset.productHandle;
              const url = swatch.dataset.productUrl;
              const pid = swatch.dataset.pimProductId || "";
              if (handle && url && !this._variantCache.has(handle)) {
                this._fetchProductVariants(handle, url, true, pid);
              }
            });
            observer.unobserve(this);
          }
        });
      },
      { rootMargin: "200px" },
    );
    observer.observe(this);
  }

  /**
   * Handle outside clicks
   * @param {Event} e
   */
  _onOutsideClick(e) {
    if (
      !this.contains(e.target) &&
      (!this.sizesList || !this.sizesList.contains(e.target))
    ) {
      if (this.sizesList) this.sizesList.classList.remove("is-open");
    }
  }

  /**
   * Delegated click handler
   * @param {Event} e
   */
  _onClick(e) {
    // Check if it's a colour swatch click
    const colourBtn = e.target.closest(".product-card__quick-buy-colour");
    if (colourBtn) {
      e.preventDefault();
      e.stopPropagation();
      this._handleColourClick(colourBtn);
      return;
    }

    // Multi-axis (Size + Fit) paged flow
    if (this._multiAxis) {
      const nav = e.target.closest(".product-card__quick-buy-nav");
      if (nav && !nav.disabled) {
        e.preventDefault();
        e.stopPropagation();
        this._goToPage(
          nav.classList.contains("product-card__quick-buy-nav--next")
            ? "fit"
            : "size",
        );
        return;
      }

      // Fit chosen → resolve the variant and add immediately
      const fitBtn = e.target.closest(".product-card__quick-buy-fit-btn");
      if (fitBtn && !fitBtn.disabled && !fitBtn.hasAttribute("data-adding")) {
        e.preventDefault();
        e.stopPropagation();
        this._handleFitSelect(fitBtn);
        return;
      }

      // Size chosen → select it and slide to the fit page
      const sizeBtn = e.target.closest(".product-card__quick-buy-btn");
      if (sizeBtn && !sizeBtn.disabled) {
        e.preventDefault();
        e.stopPropagation();
        this._handleSizeSelect(sizeBtn);
      }
      return;
    }

    // Single-axis: size button adds directly
    const sizeBtn = e.target.closest(".product-card__quick-buy-btn");
    if (sizeBtn && !sizeBtn.disabled && !sizeBtn.hasAttribute("data-adding")) {
      e.preventDefault();
      e.stopPropagation();
      this._addToCart(sizeBtn, sizeBtn.dataset.variantId);
    }
  }

  /**
   * Navigate the paged overlay between the size and fit pages.
   * @param {"size"|"fit"} page
   */
  _goToPage(page) {
    this._page = page;
    if (this.sizesList) this.sizesList.dataset.page = page;
  }

  /**
   * Reset the paged overlay back to the size step and clear selections.
   */
  _resetPaged() {
    if (!this._multiAxis) return;
    this._selectedSize = null;
    this._selectedFit = null;
    this._goToPage("size");
    this._sizeOptionsList
      ?.querySelectorAll(".product-card__quick-buy-btn--active")
      .forEach((b) =>
        b.classList.remove("product-card__quick-buy-btn--active"),
      );
  }

  /**
   * Size selected on page 1 → store it, refine fit availability, advance.
   * @param {HTMLButtonElement} btn
   */
  _handleSizeSelect(btn) {
    this._selectedSize = btn.dataset.sizeValue;

    this._sizeOptionsList
      ?.querySelectorAll(".product-card__quick-buy-btn")
      .forEach((b) =>
        b.classList.toggle("product-card__quick-buy-btn--active", b === btn),
      );

    this._refreshFitAvailability();
    this._goToPage("fit");
  }

  /**
   * Enable/disable fit buttons based on the selected colour + size.
   */
  _refreshFitAvailability() {
    if (!this._fitOptionsList) return;
    const color = this._selectedColor?.trim().toLowerCase();
    const size = this._selectedSize?.trim().toLowerCase();

    this._fitOptionsList
      .querySelectorAll(".product-card__quick-buy-fit-btn")
      .forEach((btn) => {
        const fit = btn.dataset.fitValue?.trim().toLowerCase();
        const available = this._variants.some(
          (v) =>
            v.av &&
            v[`op${this._sizeIndex}`]?.trim().toLowerCase() === size &&
            v[`op${this._fitIndex}`]?.trim().toLowerCase() === fit &&
            (!color ||
              v[`op${this._colorIndex}`]?.trim().toLowerCase() === color),
        );
        btn.disabled = !available;
        btn.classList.toggle(
          "product-card__quick-buy-btn--unavailable",
          !available,
        );
      });
  }

  /**
   * Fit selected on page 2 → resolve the colour+size+fit variant and add.
   * @param {HTMLButtonElement} btn
   */
  _handleFitSelect(btn) {
    // Guard: a size must be chosen first (e.g. user advanced via the arrow)
    if (!this._selectedSize) {
      this._goToPage("size");
      return;
    }
    this._selectedFit = btn.dataset.fitValue;
    const variant = this._resolveVariant(this._selectedSize, this._selectedFit);

    if (!variant) {
      // Should be unreachable — unavailable fits are disabled in _refreshFitAvailability.
      console.warn(
        "[QuickBuySizes] Could not resolve variant for selected colour/size/fit",
      );
      return;
    }

    this._addResolvedVariant(btn, variant);
  }

  /**
   * Find the variant matching the selected colour + size + fit.
   * @param {string|null} size
   * @param {string|null} fit
   * @returns {object|undefined}
   */
  _resolveVariant(size, fit) {
    const color = this._selectedColor?.trim().toLowerCase();
    const s = size?.trim().toLowerCase();
    const f = fit?.trim().toLowerCase();
    return this._variants.find(
      (v) =>
        v[`op${this._sizeIndex}`]?.trim().toLowerCase() === s &&
        v[`op${this._fitIndex}`]?.trim().toLowerCase() === f &&
        (!color || v[`op${this._colorIndex}`]?.trim().toLowerCase() === color),
    );
  }

  /**
   * Escape a value for safe interpolation into innerHTML (content + attributes).
   * @param {string} str
   * @returns {string}
   */
  _esc(str) {
    if (str == null) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  /**
   * Handle integrated colour swatch click
   * @param {HTMLElement} colourBtn
   */
  async _handleColourClick(colourBtn) {
    const type = colourBtn.dataset.swatchType;

    // Toggle the sizes overlay
    if (!this.sizesList) return;
    const wasOpen = this.sizesList.classList.contains("is-open");
    const wasActive = colourBtn.classList.contains(
      "product-card__quick-buy-colour--active",
    );

    // Close other quick buy size overlays
    document
      .querySelectorAll(".product-card__quick-buy-sizes.is-open")
      .forEach((el) => {
        if (el !== this.sizesList) el.classList.remove("is-open");
      });

    // If clicking the active swatch, just toggle the overlay
    if (wasActive) {
      this.sizesList.classList.toggle("is-open");
      return;
    }

    // Otherwise, open the overlay and switch colors
    this.sizesList.classList.add("is-open");

    // Update active state on all integrated swatches
    this.querySelectorAll(".product-card__quick-buy-colour").forEach((btn) => {
      btn.classList.remove("product-card__quick-buy-colour--active");
    });
    colourBtn.classList.add("product-card__quick-buy-colour--active");

    // Check if we have pre-rendered variants on the button
    if (colourBtn.dataset.variants) {
      try {
        const variants = JSON.parse(colourBtn.dataset.variants);
        const handle = colourBtn.dataset.productHandle;
        if (handle) {
          this._variantCache.set(handle, {
            variants,
            featured_image: null, // Image handled by fetch logic if needed, but we often have featured_image in JSON too
          });
        }
        this._variants = variants;
        const firstVariant = variants[0];
        this._selectedColor = firstVariant?.[`op${this._colorIndex}`] || null;
        this._rebuildSizesFromVariants();

        // Image update
        const imageUrl = firstVariant?.img;
        if (imageUrl) {
          this._updateProductImage(imageUrl);
        }
        return;
      } catch (e) {
        console.error("[QuickBuySizes] Failed to parse button variants:", e);
      }
    }

    if (type === "variant" || type === "option") {
      // Same product, different colour variant
      this._selectedColor = colourBtn.dataset.colorValue;
      this._rebuildSizesFromVariants();

      // Update image to the variant's featured image if available
      const targetColor = this._selectedColor?.trim().toLowerCase();
      const variant = this._variants.find(
        (v) => v[`op${this._colorIndex}`]?.trim().toLowerCase() === targetColor,
      );
      const imageUrl = variant?.img || variant?.featured_image;
      if (imageUrl) {
        this._updateProductImage(imageUrl);
      }
    } else if (type === "related") {
      // Different product — need its variant data
      const productHandle = colourBtn.dataset.productHandle;
      const productUrl = colourBtn.dataset.productUrl;
      const pimProductId = colourBtn.dataset.pimProductId || "";

      if (productHandle && this._variantCache.has(productHandle)) {
        const cached = this._variantCache.get(productHandle);
        this._variants = cached.variants;
        const firstVariant = cached.variants[0];
        this._selectedColor = firstVariant?.[`op${this._colorIndex}`] || null;
        this._rebuildSizesFromVariants();
        const imageUrl =
          firstVariant?.img ||
          firstVariant?.featured_image ||
          cached.featured_image;
        if (imageUrl) this._updateProductImage(imageUrl);
      } else if (productUrl) {
        await this._fetchProductVariants(
          productHandle,
          productUrl,
          false,
          pimProductId,
        );
        this._rebuildSizesFromVariants();
      }
    } else if (type === "current") {
      // Remove the data attribute to re-enable previewImage
      const productCard = this.closest("product-card");
      if (productCard)
        productCard.removeAttribute("data-disable-preview-image");
      // Switch back to the original product
      const currentHandle = this.dataset.productHandle;
      if (currentHandle && this._variantCache.has(currentHandle)) {
        this._variants = this._variantCache.get(currentHandle).variants;
      }
      this._selectedColor = this.dataset.selectedColor || null;
      this._rebuildSizesFromVariants();
      this._restoreOriginalImage();
    }
  }

  /**
   * Handle external swatch clicks (from product-card-swatches rendered below the card)
   * @param {Event} e
   */
  async _onExternalSwatchClick(e) {
    const swatch = e.target.closest(".product-card__swatch");
    if (!swatch) return;

    // Check if this is a related product swatch
    const relatedHandle = swatch.dataset.productHandle;
    const relatedUrl = swatch.dataset.productUrl;
    const relatedPim = swatch.dataset.pimProductId || "";
    const currentHandle = this.dataset.productHandle;

    const dot = swatch.querySelector("[data-color-label]");
    const color = dot ? dot.dataset.colorLabel : null;

    if (relatedHandle && relatedHandle !== currentHandle) {
      // It's a related product
      if (this._variantCache.has(relatedHandle)) {
        const cached = this._variantCache.get(relatedHandle);
        this._variants = cached.variants;
        this._selectedColor =
          color || cached.variants[0]?.[`option${this._colorIndex}`] || null;
        this._rebuildSizesFromVariants();
        if (cached.featured_image) {
          this._updateProductImage(cached.featured_image);
        }
      } else if (relatedUrl) {
        await this._fetchProductVariants(
          relatedHandle,
          relatedUrl,
          false,
          relatedPim,
        );
        // _fetchProductVariants already calls _rebuildSizesFromVariants and _updateProductImage
        if (color) {
          this._selectedColor = color;
          this._rebuildSizesFromVariants();
        }
      }
    } else {
      // It's a variant of the current product
      if (!color || color === this._selectedColor) return;

      // Ensure we are using the original product's variants
      if (currentHandle && this._variantCache.has(currentHandle)) {
        this._variants = this._variantCache.get(currentHandle).variants;
      }

      this._selectedColor = color;
      this._rebuildSizesFromVariants();

      // Update image
      const targetColor = color.trim().toLowerCase();
      const variant = this._variants.find(
        (v) => v[`op${this._colorIndex}`]?.trim().toLowerCase() === targetColor,
      );
      const imageUrl = variant?.img || variant?.featured_image;
      if (imageUrl) {
        this._updateProductImage(imageUrl);
      }
    }

    // Sync integrated swatch active state if possible
    this.querySelectorAll(".product-card__quick-buy-colour").forEach((btn) => {
      const isMatch =
        (btn.dataset.colorValue && btn.dataset.colorValue === color) ||
        (btn.dataset.productHandle &&
          btn.dataset.productHandle === relatedHandle);
      btn.classList.toggle("product-card__quick-buy-colour--active", isMatch);
    });
  }

  /**
   * Fetch a related product's variants via /products/{handle}.js
   * @param {string} handle
   * @param {string} productUrl
   * @param {boolean} [isSilent]
   * @param {string} [pimProductId] - product-level PIM id from the swatch. The .js
   *   endpoint returns no metafields, so it must be supplied and stamped onto every
   *   variant, otherwise add-to-cart sends an empty _pim_product_id (OMS failure).
   */
  async _fetchProductVariants(
    handle,
    productUrl,
    isSilent = false,
    pimProductId = "",
  ) {
    try {
      const res = await fetch(`${productUrl}.js`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const variants = data.variants.map((v) => ({
        id: v.id,
        op1: v.option1,
        op2: v.option2,
        op3: v.option3,
        av: v.available,
        img: v.featured_image?.src || v.featured_image,
        pid: pimProductId,
      }));
      const featured_image = data.featured_image || null;

      if (handle) {
        this._variantCache.set(handle, { variants, featured_image });
      }

      if (isSilent) return;

      this._variants = variants;
      // Set colour from the related product's variants
      this._selectedColor = variants[0]?.[`op${this._colorIndex}`] || null;
      this._rebuildSizesFromVariants();
      if (featured_image) this._updateProductImage(featured_image);
    } catch (err) {
      console.error("[QuickBuySizes] Failed to fetch product:", err);
    }
  }

  /**
   * Update the product card image
   * @param {string} url
   */
  _updateProductImage(url) {
    if (!url) return;
    const productCard = this.closest("product-card");
    if (!productCard) return;

    console.log("[QuickBuySizes] Updating product image:", url);

    // Update all images in the card to ensure it's visible if there's a slideshow
    const images = productCard.querySelectorAll(".product-media__image");
    images.forEach((img) => {
      img.src = url;
      img.removeAttribute("srcset");
    });
  }

  /**
   * Restore the original product card image
   */
  _restoreOriginalImage() {
    const productCard = this.closest("product-card");
    if (!productCard) return;

    const img = productCard.querySelector(".product-media__image");
    if (!img || !this._originalImageSrc) return;

    img.src = this._originalImageSrc;
    if (this._originalImageSrcset) {
      img.setAttribute("srcset", this._originalImageSrcset);
    }
  }

  /**
   * Rebuild size buttons entirely from the current _variants array.
   * Used when switching to a related product whose sizes may differ.
   */
  _rebuildSizesFromVariants() {
    const sizesList = this.sizesList;
    if (!sizesList) return;

    // Multi-axis: rebuild both pages' options for the current colour, reset to step 1
    if (this._multiAxis) {
      this._rebuildMultiAxis();
      return;
    }

    // option1 = Size, option2 = Color (matches Shopify variant order)

    // 1. Collect ALL unique sizes from ALL variants so every size is always shown
    const allSizeMap = new Map();
    this._variants.forEach((v) => {
      const size = v[`op${this._sizeIndex}`]?.trim();
      if (size && !allSizeMap.has(size)) {
        allSizeMap.set(size, v);
      }
    });

    // 2. Filter variants for the selected colour (used for availability checks)
    let colorVariants = this._variants;
    if (this._selectedColor) {
      const targetColor = this._selectedColor.trim().toLowerCase();
      colorVariants = this._variants.filter(
        (v) => v[`op${this._colorIndex}`]?.trim().toLowerCase() === targetColor,
      );
    }

    let html = "";
    if (allSizeMap.size > 0) {
      const targetColor = this._selectedColor?.trim().toLowerCase();
      allSizeMap.forEach((fallbackVariant, size) => {
        const targetSize = size.trim().toLowerCase();
        // Check if this size is available in the selected colour
        const colorMatch = colorVariants.find(
          (v) => v[`op${this._sizeIndex}`]?.trim().toLowerCase() === targetSize,
        );
        const available = colorVariants.some(
          (v) =>
            v[`op${this._sizeIndex}`]?.trim().toLowerCase() === targetSize &&
            v.av,
        );
        const bestVariant =
          colorVariants.find(
            (v) =>
              v[`op${this._sizeIndex}`]?.trim().toLowerCase() === targetSize &&
              v.av,
          ) ||
          colorMatch ||
          fallbackVariant;

        html += `<li class="product-card__quick-buy-size">
          <button
            type="button"
            class="product-card__quick-buy-btn${!available ? " product-card__quick-buy-btn--unavailable" : ""}"
            data-variant-id="${bestVariant.id}"
            data-size-value="${size}"
            data-pim-product-id="${bestVariant.pid || ""}"
            ${!available ? "disabled" : ""}
          >${size} </button>
        </li>`;
      });
    } else {
      // If no sizes, show a disabled button for clarity
      html = `<li class="product-card__quick-buy-size">
        <button type="button" class="product-card__quick-buy-btn product-card__quick-buy-btn--unavailable" disabled>No sizes available</button>
      </li>`;
    }

    // Debug logging
    console.log("[QuickBuySizes] _rebuildSizesFromVariants:", {
      selectedColor: this._selectedColor,
      variants: this._variants,
      colorVariants,
      allSizeMap: Array.from(allSizeMap.entries()),
      html: html.substring(0, 100) + "...",
    });

    sizesList.innerHTML = html;
  }

  /**
   * Rebuild the size and fit pages for the current colour, then reset to step 1.
   * Availability on the size page is "any fit"; the fit page is refined once a
   * size is chosen (_refreshFitAvailability).
   */
  _rebuildMultiAxis() {
    const color = this._selectedColor?.trim().toLowerCase();

    // Collect unique size + fit values, preserving first-seen order
    const sizeValues = [];
    const sizeSeen = new Set();
    const fitValues = [];
    const fitSeen = new Set();
    this._variants.forEach((v) => {
      const s = v[`op${this._sizeIndex}`]?.trim();
      if (s && !sizeSeen.has(s)) {
        sizeSeen.add(s);
        sizeValues.push(s);
      }
      const f = v[`op${this._fitIndex}`]?.trim();
      if (f && !fitSeen.has(f)) {
        fitSeen.add(f);
        fitValues.push(f);
      }
    });

    const colorMatch = (v) =>
      !color || v[`op${this._colorIndex}`]?.trim().toLowerCase() === color;

    if (this._sizeOptionsList) {
      this._sizeOptionsList.innerHTML = sizeValues
        .map((size) => {
          const target = size.toLowerCase();
          const available = this._variants.some(
            (v) =>
              v.av &&
              v[`op${this._sizeIndex}`]?.trim().toLowerCase() === target &&
              colorMatch(v),
          );
          const safe = this._esc(size);
          return `<li class="product-card__quick-buy-size">
            <button type="button" class="product-card__quick-buy-btn${!available ? " product-card__quick-buy-btn--unavailable" : ""}" data-size-value="${safe}" ${!available ? "disabled" : ""}>${safe}</button>
          </li>`;
        })
        .join("");
    }

    if (this._fitOptionsList) {
      this._fitOptionsList.innerHTML = fitValues
        .map((fit) => {
          const target = fit.toLowerCase();
          const available = this._variants.some(
            (v) =>
              v.av &&
              v[`op${this._fitIndex}`]?.trim().toLowerCase() === target &&
              colorMatch(v),
          );
          const safe = this._esc(fit);
          return `<li class="product-card__quick-buy-size">
            <button type="button" class="product-card__quick-buy-btn product-card__quick-buy-fit-btn${!available ? " product-card__quick-buy-btn--unavailable" : ""}" data-fit-value="${safe}" ${!available ? "disabled" : ""}>${safe}</button>
          </li>`;
        })
        .join("");
    }

    this._resetPaged();
  }

  /**
   * Add a variant resolved from the multi-axis (colour + size + fit) selection.
   * Properties are read from the variant object baked into the variants JSON.
   * @param {HTMLButtonElement} btn
   * @param {object} variant
   */
  async _addResolvedVariant(btn, variant) {
    const properties = {
      _pim_product_id: variant.pid || btn.dataset.pimProductId || "",
    };
    this._submitAddToCart(btn, String(variant.id), properties);
  }

  /**
   * Fetch add-to-cart
   * @param {HTMLButtonElement} btn
   * @param {string} variantId
   */
  async _addToCart(btn, variantId) {
    if (!variantId) return;

    // Localised resolves the rest server-side from the PIM id + checkout market.
    const properties = {
      _pim_product_id: btn.dataset.pimProductId || "",
    };

    this._submitAddToCart(btn, variantId, properties);
  }

  /**
   * Shared add-to-cart request + success/error UI for both the single-axis
   * and multi-axis (paged) flows.
   * @param {HTMLButtonElement} btn
   * @param {string} variantId
   * @param {object} properties
   */
  async _submitAddToCart(btn, variantId, properties) {
    if (!variantId) return;

    // Safety net: a missing PIM id silently fails the order in OMS, so flag it
    // (Sentry) before the add-to-cart request goes out.
    if (!properties || !properties._pim_product_id) {
      window.Sentry?.captureMessage(
        `Quick Buy: missing _pim_product_id (variant ${variantId})`,
        {
          level: "error",
          extra: { variantId, productHandle: this.dataset.productHandle },
        },
      );
    }

    const feedback = this.querySelector(".product-card__quick-buy-feedback");
    const originalText = btn.textContent;

    btn.setAttribute("data-adding", "");
    if (feedback) feedback.textContent = "";

    try {
      const cartItemsComponents = document.querySelectorAll(
        "cart-items-component",
      );
      const sectionsToUpdate = /** @type {string[]} */ ([]);
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.push(item.dataset.sectionId);
        }
      });

      const res = await fetch(
        (typeof Theme !== "undefined" && Theme.routes?.cart_add_url) ||
          "/cart/add.js",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: [{ id: parseInt(variantId, 10), quantity: 1, properties }],
            sections: sectionsToUpdate.join(","),
          }),
        },
      );

      const data = await res.json();
      btn.removeAttribute("data-adding");

      if (data.status) {
        if (feedback) feedback.textContent = data.message || "Could not add";
        setTimeout(() => {
          if (feedback) feedback.textContent = "";
        }, 4000);
        return;
      }

      // Success
      btn.setAttribute("data-added", "");
      btn.textContent = "✓";

      // Close overlay on success and reset the paged flow back to step 1
      if (this.sizesList) this.sizesList.classList.remove("is-open");
      this._resetPaged();

      console.log("[QuickBuy] Item added successfully", data);

      // Update cart bubble/total first
      document.dispatchEvent(
        new CustomEvent("cart:update", {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: "quick-buy-sizes",
            data: {
              source: "product-form-component",
              itemCount: 1,
              productId: this.dataset.productId,
              sections: data.sections,
            },
          },
        }),
      );

      // Open cart drawer
      if (document.body && document.body.dataset.template !== "cart") {
        const cartDrawer = document.querySelector("cart-drawer");
        console.log("[QuickBuy] Attempting to open drawer", cartDrawer);
        if (cartDrawer && typeof cartDrawer.openDrawer === "function") {
          cartDrawer.openDrawer();
        } else if (cartDrawer) {
          console.warn(
            "[QuickBuy] cart-drawer found but openDrawer method missing. Element might not be upgraded.",
          );
          cartDrawer.setAttribute("aria-expanded", "true");
        } else {
          console.error("[QuickBuy] cart-drawer element not found in DOM");
        }
      }

      setTimeout(() => {
        btn.removeAttribute("data-added");
        btn.textContent = originalText;
      }, 1200);
    } catch (err) {
      btn.removeAttribute("data-adding");
      if (feedback) feedback.textContent = "Something went wrong";
      setTimeout(() => {
        if (feedback) feedback.textContent = "";
      }, 4000);
    }
  }
}

if (!customElements.get("quick-buy-sizes")) {
  customElements.define("quick-buy-sizes", QuickBuySizes);
}
